/** Indicadores do treino de força (A5): aderência, volume semanal e evolução por exercício. */
import { useState } from "react";
import { api } from "../api/client";
import type { StrengthOverview as Overview } from "../api/types";
import { addDays, iso } from "../lib/training";
import { formatDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import { exerciseKey, hasStrengthData, historyPoints, kgText, pickExercise, defaultMetric, type ChartMetric } from "../lib/strengthStats";
import { BarChart, ChartFrame, TimeChart } from "./charts";
import { Kpi } from "./performance";
import { ErrorState, Loading } from "./ui";
import { LOCALE, t, tn } from "../i18n";

export function StrengthOverviewSection({ path, weeks }: { path: string; weeks: number }) {
  const days = weeks * 7;           // segue o filtro de período da página
  const [sel, setSel] = useState<string | null>(null);
  const [metric, setMetric] = useState<ChartMetric | null>(null);
  const to = iso(new Date());
  const from = iso(addDays(new Date(), -(days - 1)));
  const q = useAsync(() => api.get<Overview>(path, { from, to }), [path, days]);
  const o = q.data;
  const current = pickExercise(o, sel);
  const m: ChartMetric = metric ?? (current ? defaultMetric(current) : "max_load_kg");
  const short = (d: string) => formatDate(d).slice(0, 5);
  return (
    <section className="section" aria-labelledby="forca-ev">
      <h2 id="forca-ev" style={{ marginBottom: 10 }}>{t("Treino de força")}</h2>
      {q.error && <ErrorState error={q.error} onRetry={q.reload} />}
      {!o && q.loading && <Loading />}
      {o && !hasStrengthData(o) && <p className="muted">{t("Nenhum treino de força registrado neste período.")}</p>}
      {o && hasStrengthData(o) && (
        <div className={q.loading ? "refetching" : undefined}>
          <dl className="kpis">
            <Kpi label={t("Treinos com registro")} value={String(o.totals.workouts)} />
            <Kpi
              label={t("Séries registradas")}
              value={String(o.totals.sets_logged)}
              sub={t("Feitas: {a} · Parciais: {b} · Puladas: {c}", { a: o.totals.sets_done, b: o.totals.sets_partial, c: o.totals.sets_skipped })}
            />
            <Kpi label={t("Volume (repetições × carga)")} value={kgText(o.totals.volume_kg, LOCALE)} sub={tn("{n} repetição", "{n} repetições", o.totals.reps)} />
            <Kpi
              label={t("Aderência às séries prescritas")}
              value={o.totals.adherence_pct !== null ? `${o.totals.adherence_pct} %` : "—"}
              sub={o.totals.sets_prescribed ? t("{a} de {b} séries", { a: o.totals.sets_recorded_of_prescribed, b: o.totals.sets_prescribed }) : t("Sem treinos de força vencidos")}
              meter={o.totals.adherence_pct !== null ? o.totals.adherence_pct / 100 : null}
            />
          </dl>
          <div className="chart-grid">
            <ChartFrame
              title={t("Volume de força por semana")}
              subtitle={t("Soma de repetições × carga das séries feitas ou parciais. Séries puladas não entram.")}
              table={{ head: [t("Semana"), t("Volume (kg)"), t("Séries"), t("Treinos")], rows: o.weeks.map((w) => [short(w.week_start), w.volume_kg, w.sets, w.workouts]) }}
              empty={o.weeks.length === 0 ? t("Nenhuma série registrada no período.") : null}
            >
              <BarChart
                data={o.weeks.map((w) => ({
                  key: w.week_start, label: short(w.week_start), title: t("Semana de {week_start}", { week_start: short(w.week_start) }),
                  value: w.volume_kg || null, extra: t("{a} séries em {b} treinos", { a: w.sets, b: w.workouts }),
                }))}
                fmt={(v) => kgText(v, LOCALE)}
                axisFmt={(v) => String(Math.round(v))}
                valueLabel={t("volume")}
              />
            </ChartFrame>
            {current && (
              <ChartFrame
                title={t("Evolução: {name}", { name: current.name })}
                subtitle={m === "est_1rm_kg" ? t("Estimativa de 1RM (fórmula de Epley, só com 1 a 12 repetições). Não é um teste de 1RM.") : undefined}
                table={{ head: [t("Data"), t("Carga máx. (kg)"), t("1RM estimada (kg)"), t("Volume (kg)"), t("Séries")], rows: current.history.map((h) => [formatDate(h.date), h.max_load_kg ?? "—", h.est_1rm_kg ?? "—", h.volume_kg, h.sets]) }}
                empty={historyPoints(current, m).length === 0 ? t("Sem dados deste exercício para a métrica escolhida.") : null}
              >
                <TimeChart
                  points={historyPoints(current, m).map((p) => ({ key: p.date, date: p.date, value: p.value, title: formatDate(p.date) }))}
                  fmt={(v) => kgText(v, LOCALE)}
                  valueLabel={m === "volume_kg" ? t("volume") : m === "est_1rm_kg" ? t("1RM estimada") : t("carga máxima")}
                  from={current.history[0]?.date ?? from}
                  to={current.history[current.history.length - 1]?.date ?? to}
                />
              </ChartFrame>
            )}
          </div>
          <div className="panel section">
            <div className="panel-head">
              <h3>{t("Por exercício")}</h3>
              {current && (
                <div className="segmented" role="group" aria-label={t("Métrica do gráfico")}>
                  {([["max_load_kg", t("Carga máx.")], ["est_1rm_kg", t("1RM estimada")], ["volume_kg", t("Volume")]] as [ChartMetric, string][]).map(([k, l]) => (
                    <button key={k} type="button" aria-pressed={m === k} onClick={() => setMetric(k)}>{l}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t("Exercício")}</th><th className="num">{t("Séries")}</th><th className="num">{t("Repetições")}</th>
                    <th className="num">{t("Carga máx.")}</th><th className="num">{t("1RM estimada")}</th><th className="num">{t("Volume")}</th><th>{t("Última vez")}</th>
                  </tr>
                </thead>
                <tbody>
                  {o.exercises.map((e) => (
                    <tr key={exerciseKey(e)} className={current && exerciseKey(e) === exerciseKey(current) ? "is-selected" : undefined}>
                      <td>
                        <button type="button" className="link-btn" onClick={() => { setSel(exerciseKey(e)); setMetric(null); }}
                          aria-pressed={!!current && exerciseKey(e) === exerciseKey(current)}>{e.name}</button>
                      </td>
                      <td className="num">{e.sets}</td><td className="num">{e.reps}</td>
                      <td className="num">{kgText(e.max_load_kg, LOCALE)}</td><td className="num">{kgText(e.est_1rm_kg, LOCALE)}</td>
                      <td className="num">{kgText(e.volume_kg, LOCALE)}</td><td>{formatDate(e.last_date)}</td>
                    </tr>
                  ))}
                  {o.exercises.length === 0 && <tr><td colSpan={7} className="muted">{t("Nenhuma série feita no período.")}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
